import React, { useCallback, useState } from 'react';
import { RejectNotesModal } from './RowActionMenu';
import { useNotifications } from '../../context/NotificationContext';

const BUSY_LABEL = {
  submitted: 'Submitting...',
  validated: 'Validating...',
  approved: 'Approving...',
  rejected: 'Rejecting...',
};

// Submit / Validate / Approve / Reject quick actions for a table's
// ActionDropdown, through a collection API's dedicated endpoints
// (api.submit / validate / approve / reject). Which of them the menu offers
// comes from the table's WORKFLOW_RULES entry.
//
//   const wf = useRowWorkflowActions({ api, rules, moduleKey, roleId, onChanged, onError, labelOf });
//   <ActionDropdown … onSubmit={() => wf.run(row, 'submitted')} onReject={() => wf.askReject(row)} />
//   {wf.rejectModal}
export default function useRowWorkflowActions({
  api,
  rules,
  moduleKey,
  roleId,
  onChanged,
  onError,
  labelOf = (row) => row?.name ?? row?.uuid,
}) {
  const [busy, setBusy] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const { pushWorkflowNotification } = useNotifications();

  const run = useCallback(async (row, status, notes = '') => {
    const uuid = row?.uuid ?? row?.id;
    if (!uuid) return;
    setBusy(BUSY_LABEL[status] ?? 'Saving...');
    try {
      if (status === 'submitted') await api.submit(uuid);
      else if (status === 'validated') await api.validate(uuid);
      else if (status === 'approved') await api.approve(uuid);
      else if (status === 'rejected') await api.reject(uuid, notes);
      pushWorkflowNotification?.({
        moduleKey, roleCan: rules?.roleCan, newStatus: status,
        actorRoleId: roleId, recordLabel: labelOf(row), notes,
      });
      await onChanged?.(row, status);
    } catch (err) {
      onError?.(err?.response?.data?.message || err?.message || `Failed to update the record to ${status}.`);
    } finally {
      setBusy('');
    }
  }, [api, rules, moduleKey, roleId, onChanged, onError, labelOf, pushWorkflowNotification]);

  const confirmReject = useCallback(async (notes) => {
    if (!rejectTarget) return;
    await run(rejectTarget, 'rejected', notes);
    setRejectTarget(null);
  }, [rejectTarget, run]);

  const rejectModal = rejectTarget ? (
    <RejectNotesModal onConfirm={confirmReject} onClose={() => setRejectTarget(null)} />
  ) : null;

  return { busy, run, askReject: setRejectTarget, rejectModal };
}
